import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Search, RefreshCw } from 'lucide-react';

interface AuditLog {
  id: number;
  username: string | null;
  action: string;
  status: 'success' | 'failed' | 'warning';
  ip_address: string | null;
  created_at: string;
  details: string | null;
}

const ACTION_LABELS: Record<string, string> = {
  'auth.login': 'Login', 'auth.logout': 'Logout', 'auth.2fa_requested': '2FA Sent',
  'auth.2fa_verified': '2FA Verified', 'auth.password_reset': 'Password Reset',
  'auth.password_changed': 'Password Changed', 'user.create': 'User Created',
  'user.update': 'User Updated', 'user.delete': 'User Deleted',
};

export default function AuditLogs() {
  const { token } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/audit-logs?action=${filter}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setLogs(data.logs);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { fetchLogs(); }, [filter]);

  const filtered = logs.filter(l => 
    !search || l.username?.toLowerCase().includes(search.toLowerCase()) ||
    l.action.includes(search) || l.ip_address?.includes(search)
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Shield className="h-6 w-6 text-indigo-600" /> Audit Logs
        </h1>
        <button onClick={fetchLogs} className="btn btn-secondary flex items-center gap-2">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="bg-white shadow rounded p-4 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input 
            type="text" 
            placeholder="Search by user, action, IP..." 
            value={search} 
            onChange={e => setSearch(e.target.value)}
            className="pl-10 w-full h-10 text-sm text-gray-900 bg-white border border-gray-300 rounded"
          />
        </div>
        <select 
          value={filter} 
          onChange={e => setFilter(e.target.value)} 
          className="h-10 px-3 text-sm text-gray-900 bg-white border border-gray-300 rounded"
        >
          <option value="">All Actions</option>
          <option value="auth">Auth Events</option>
          <option value="user">User Events</option>
          <option value="system">System Events</option>
        </select>
      </div>

      <div className="bg-white shadow rounded overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Time</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">User</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Action</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">IP</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500">No logs found</td></tr>
            ) : filtered.map(log => (
              <tr key={log.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm text-gray-600">{new Date(log.created_at).toLocaleString()}</td>
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.username || 'System'}</td>
                <td className="px-4 py-3 text-sm text-gray-700">{ACTION_LABELS[log.action] || log.action}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    log.status === 'success' ? 'bg-green-100 text-green-700' :
                    log.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>{log.status}</span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{log.ip_address || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
